import Marionette from "backbone.marionette";
import Radio from "backbone.radio";
import { errorParser } from "../../../../utils";
import _ from "underscore";

import NoteEditorBehavior from "../../../../behaviors/note-editor";
import TooltipBehavior from "../../../../behaviors/tooltip";
import PopoverBehavior from "../../../../behaviors/popover";
import FormSubmissionBehavior from "../../../../behaviors/form-submission";
import ChosenBehavior from "../../../../behaviors/chosen";

import templates from "../../../../utils/templates";
import simpleAlert from "../../../../utils/alerts/simple";

const channel = Radio.channel("global");

const DEFAULTS = {
  tagName: "form",
  template: templates.find("chart_reviews", "chart_edit_drg_deletes_edit_diag"),
};

class View extends Marionette.ItemView {
  constructor(options = {}) {
    _.defaults(options, DEFAULTS);
    super(options);
  }

  behaviors() {
    return [
      { behaviorClass: ChosenBehavior },
      { behaviorClass: FormSubmissionBehavior },
      {
        behaviorClass: NoteEditorBehavior,
        opts: {
          modelProperty: "notesList",
          service: "chart-reviews:drg-deletes-notes",
          config: {
            id: this.options.model.get("id"),
          },
        },
      },
      { behaviorClass: TooltipBehavior },
      {
        behaviorClass: PopoverBehavior,
        opts: {
          html: true,
          placement: "auto left",
        },
      },
    ];
  }

  ui() {
    return {
      auxType: "#auxType",
      claimCommercialPlanId: "#claimCommercialPlanId",
      customerReferenceNumber: "#customerReferenceNumber",
      sourceReferenceNumber: "#sourceReferenceNumber",
      providerReferenceNumber: "#providerReferenceNumber",
      assessmentId: "#assessmentId",
      diagRenderingProviderName: "#diagRenderingProviderName",
      diagRenderingProviderNpi: "#diagRenderingProviderNpi",
      disposition: "#disposition",
      placeOfServiceCode: "#placeOfServiceCode",
      supplementalReasonCode: "#supplementalReasonCode",
      action: "#action",
      // buttons
      undoBtn: "button.undo",
    };
  }

  events() {
    return {
      submit: "_onSubmit",
      "change @ui.auxType": "_handleTextDropdown",
      "keyup @ui.claimCommercialPlanId": "_uppercase",
      "change @ui.claimCommercialPlanId": "_handleText",
      "change @ui.customerReferenceNumber": "_handleText",
      "change @ui.sourceReferenceNumber": "_handleText",
      "change @ui.providerReferenceNumber": "_handleText",
      "change @ui.assessmentId": "_handleText",
      "change @ui.diagRenderingProviderName": "_handleText",
      "change @ui.diagRenderingProviderNpi": "_handleText",
      "change @ui.disposition": "_handleTextDropdown",
      "change @ui.placeOfServiceCode": "_handleTextDropdown",
      "change @ui.supplementalReasonCode": "_handleTextDropdown",
      "change @ui.action": "_handleTextDropdown",
      // buttons
      "click @ui.undoBtn": _.debounce(this._onUndo, 200),
    };
  }

  initialize() {
    // make sure to render the view when the UI model changes
    this.listenTo(this.model, "change", this._onUIModelChange);
    this.listenTo(this.model.getNotesList(), "update", this._onUIModelChange);

    this.listenTo(this.model, "change:disposition", this._onDispositionChange);
    this.listenTo(this.model, "change:auxType", this._onAuxTypeChange);
    this.listenTo(
      this.model,
      "change:placeOfServiceCode",
      this._onPlaceOfServiceCodeChange
    );
    this.listenTo(
      this.model,
      "change:supplementalReasonCode",
      this._onSupplementalReasonCodeChange
    );
    this.listenTo(this.model, "change:action", this._onActionChange);

    // localize copy of provided API model
    this.apiModel = this.options.apiModel.clone();

    // listen for changes to API model
    this.listenTo(this.apiModel, "change", this._onAPIModelChange);
    this.listenTo(this.apiModel, "sync error", this._onAPIModelSync);
  }

  onAttach() {
    this._notifyBehaviors();
  }

  _onUIModelChange() {
    // render the component
    this.render();

    this._notifyBehaviors();
  }

  _notifyBehaviors() {
    this.triggerMethod("ModelChange", this.model);
    this.triggerMethod("ContentRendered");
  }

  _onAPIModelChange(model) {
    // apply API model changes to UI model
    this.model.set(model.changed);
  }

  // handles the model's successful and failed sync. It notifies behaviors
  // like FormSubmission that the form submission has completed.
  _onAPIModelSync() {
    this.triggerMethod("FormSubmissionComplete");
  }

  // keep
  _onDispositionChange() {
    // get list of options from model
    const options = this.model.getDispositions();
    // select the active option
    options.map((item) => {
      const match = item.getKey() === this.model.getDisposition();
      item.setChecked(match);
    });
    // update model with updated options
    this.model.setDispositions(options);
  }

  // keep
  _onAuxTypeChange() {
    // get list of options from model
    const options = this.model.getAuxTypes();
    // select the active option
    options.map((item) => {
      const match = item.getKey() === this.model.getAuxType();
      item.setChecked(match);
    });
    // update model with updated options
    this.model.setAuxTypes(options);
  }

  _onPlaceOfServiceCodeChange() {
    // get list of options from model
    const options = this.model.getPlacesOfService();
    // select the active option
    options.map((item) => {
      const match = item.getKey() === this.model.getPlaceOfServiceCode();
      item.setChecked(match);
    });
    // update model with updated options
    this.model.setPlacesOfService(options);
  }

  _onSupplementalReasonCodeChange() {
    // get list of options from model
    const options = this.model.getSupplementalReasonCodes();
    // select the active option
    options.map((item) => {
      const match = item.getKey() === this.model.getSupplementalReasonCode();
      item.setChecked(match);
    });
    // update model with updated options
    this.model.setSupplementalReasonCodes(options);
  }

  _onActionChange() {
    // get list of options from model
    const options = this.model.getActions();
    // select the active option
    options.map((item) => {
      const match = item.getKey() === this.model.getAction();
      item.setChecked(match);
    });
    // update model with updated options
    this.model.setActions(options);
  }

  _handleText(e) {
    const $el = $(e.target);
    const text = $el.val().trim();

    // update property on model
    this.apiModel.set($el[0].id, !_.isEmpty(text) ? text : null);
  }

  _uppercase(e) {
    const $el = $(e.target);
    const text = $el.val().trim().toUpperCase();
    $el.val(text);
  }

  _handleCheckbox(e) {
    const $el = $(e.target);
    const checked = $el[0].checked;

    // update property on model
    this.apiModel.set($el[0].id, checked);
  }

  _handleTextDropdown(e) {
    const $el = $(e.target);
    const selectedOptions = $(":selected", $el);
    const selected = selectedOptions.map((index, option) => option.value);
    const value = selected.length > 1 ? selected.join(",") : selected.get(0);

    // update property on model
    this.apiModel.set($el[0].id, value);
  }

  _handleSingleNumericDropdown(e) {
    const $el = $(e.target);
    const selectedOptions = $(":selected", $el);
    const selected = selectedOptions.map((index, option) => +option.value);
    const value = selected.length > 1 ? selected : selected.get(0) || null;

    // update property on model
    this.apiModel.set($el[0].id, value);
  }

  _handleMultipleNumericDropdown(e) {
    const $el = $(e.target);
    const selectedOptions = $(":selected", $el);
    const selected =
      selectedOptions.map((index, option) => +option.value) || [];

    // update property on model
    this.apiModel.set($el[0].id, selected);
  }

  _onSubmit(e) {
    e.preventDefault();

    const keys = {
      id: this.apiModel.getId(),
    };

    // extract submitted form data and merge with keys
    let formData = Object.assign({}, Backbone.Syphon.serialize(this), keys);
    // clean up fields added by KendoUI
    delete formData.clientClaimNbr_input;

    // remove falsy values, excludes booleans
    formData = _.omit(formData, (value) => {
      return !_.isBoolean(value) && _.isEmpty(value);
    });

    // set clean formData into model
    this.apiModel.set(formData);

    // validate it
    if (this.apiModel.isValid()) {
      // attempt to update DRG
      this.apiModel.save(
        {},
        {
          success: this._onSync.bind(this),
          error: this._onSyncFailure.bind(this),
        }
      );
    } else {
      // show warning message
      channel.trigger("status:show", {
        type: "warning",
        message: this.apiModel.validationError,
      });
    }
  }

  _onSync() {
    // show success message
    channel.trigger("status:show", {
      type: "success",
      message: "Your Diag was updated successfully.",
    });

    // notify parent layout of this change
    this.triggerMethod("modal:close");
  }

  _onSyncFailure(model, response) {
    let message = errorParser(response);
    try {
      message = response.responseJSON.error["error-message"];
    } catch (e) {
      console.warn("Unable to find additional info on thrown exception.");
    }

    // show error
    simpleAlert({
      type: "error",
      title: "Your changes could not be saved.",
      text: message,
    });
  }

  _onUndo() {
    // update model with original API model
    this.apiModel.set(this.options.apiModel.toJSON());

    // show success message
    channel.trigger("status:show", {
      type: "success",
      message: "This Diag has been reverted to its original values.",
    });
  }
}

export { View as default };
